/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { SearchMaterials } from "contexts/search/context";
import { useMemo } from "react";
import { PeriodMaterialSummary } from "store/api/generatedApi";
import { roundedInventory } from "../common/inventoryTotals";

export interface PlanTableOptions {
  productGroups: boolean;
  percentages: boolean;
  forecast: boolean;
  steelGrades: string[];
}

type MaterialQuantities = Record<number, number>;

export interface PlanConsumptionAggregation {
  inventoryInitial: PlanConsumption;
  consumption: PlanConsumption;
  inventoryFinal: PlanConsumption;
  obtainableRequired: PlanConsumption;
  obtainableRemaining: PlanConsumption;
}

export interface PlanConsumption {
  materials: MaterialQuantities;
  total: number;
  maximum: number;
  price: number;
}

export type PlanMaterial =
  { material: number; summary?: undefined } | { summary: "total" };

export const useInventoryContent = (
  materials: SearchMaterials,
  materialSummary: PeriodMaterialSummary[]
): PlanConsumptionAggregation => {
  return useMemo(() => {
    const consumption = {
      inventoryInitial: buildConsumption(
        materials,
        materialSummary,
        (summary) => summary.initial_inventory
      ),
      consumption: buildConsumption(
        materials,
        materialSummary,
        (summary) => summary.mass_consumed
      ),
      inventoryFinal: buildConsumption(
        materials,
        materialSummary,
        (summary) => summary.final_inventory
      ),
      obtainableRequired: buildConsumption(
        materials,
        materialSummary,
        (summary) => summary.obtainable_obtained
      ),
      obtainableRemaining: buildConsumption(
        materials,
        materialSummary,
        (summary) => summary.obtainable_remaining
      ),
    };

    return consumption;
  }, [materials, materialSummary]);
};

const buildConsumption = (
  searchMaterials: SearchMaterials,
  summaries: PeriodMaterialSummary[],
  get: (summary: PeriodMaterialSummary) => number
): {
  materials: MaterialQuantities;
  total: number;
  maximum: number;
  price: number;
} => {
  const { byMaterial, total } = roundedInventory({
    summaries,
    material: (summary) => searchMaterials.byName[summary.material]!.id,
    quantity: get,
  });
  const materials = Object.fromEntries(byMaterial);

  return {
    materials,
    total,
    maximum: Math.max(...byMaterial.values()),
    price: -1,
  };
};
